<template>
    <v-container fluid class="pa-6">
        <!-- Header & General Info -->
        <v-card class="mb-6 elevation-2 rounded-lg overflow-hidden border-top-primary">
            <v-card-title class="d-flex align-center py-4 px-6 bg-gradient-header white--text">
                <v-icon large color="white" class="mr-3">mdi-recycle</v-icon>
                <div>
                    <div class="text-h5 font-weight-bold">Gestión y Prorrateo de Verde Proceso</div>
                    <div class="text-subtitle-2 opacity-80">Distribución de descarte del turno activo</div>
                </div>
                <v-spacer></v-spacer>
                <v-chip color="rgba(255,255,255,0.2)" text-color="white" class="font-weight-bold px-4">
                    <v-icon left small color="white">mdi-clock-outline</v-icon>
                    {{ activeTurnName }}
                </v-chip>
            </v-card-title>
            <v-card-text class="py-4 px-6 grey lighten-4">
                <v-row align="center">
                    <v-col cols="12" md="4" sm="6" class="py-0">
                        <s-select-definition
                            v-model="activeTypeCultive"
                            :def="1173"
                            label="Cultivo"
                            @input="search()"
                        />
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <!-- Main Actions & Data Grid -->
        <v-card class="elevation-2 rounded-lg overflow-hidden">
            <v-card-title class="d-flex align-center py-4 px-6 white border-bottom">
                <div class="text-h6 font-weight-bold text-primary-dark">Registros de Descarte Verde Proceso</div>
                <v-spacer></v-spacer>
                <v-btn
                    color="success"
                    class="px-5 py-5 rounded-lg font-weight-bold"
                    :disabled="selectedRows.length === 0"
                    @click="showProrrateoModal = true"
                    elevation="2"
                >
                    <v-icon left>mdi-calculator-variant</v-icon>
                    Prorratear Selección ({{ selectedRows.length }})
                </v-btn>
            </v-card-title>

            <v-data-table
                :headers="headers"
                :items="items"
                :loading="loading"
                loading-text="Cargando información del verde proceso..."
                show-select
                v-model="selectedRows"
                item-key="DlpID"
                class="elevation-0 green-process-table"
                no-data-text="No hay transferencias de Verde Proceso registradas en este turno."
                :footer-props="{
                    'items-per-page-options': [15, 30, 50, -1],
                    'items-per-page-text': 'Filas por página'
                }"
                :items-per-page="15"
            >
                <!-- DlpID column -->
                <template v-slot:item.DlpID="{ item }">
                    <span class="font-weight-medium grey--text text--darken-1">#{{ item.DlpID }}</span>
                </template>

                <!-- Line column -->
                <template v-slot:item.LineName="{ item }">
                    <span class="font-weight-bold text-primary-dark">{{ item.LineName }}</span>
                </template>

                <!-- Period column -->
                <template v-slot:item.PeriodNumber="{ item }">
                    <v-chip v-if="item.PeriodNumber" small color="teal lighten-5" class="teal--text text--darken-3 font-weight-medium">
                        P{{ item.PeriodNumber }}
                    </v-chip>
                    <span v-else class="grey--text text-caption">-</span>
                </template>

                <!-- Jabas column -->
                <template v-slot:item.JabasVerdes="{ item }">
                    <span class="font-weight-bold">{{ item.JabasVerdes }}</span>
                </template>

                <!-- Approx Weight column with Inline Edit -->
                <template v-slot:item.ApproxWeight="{ item }">
                    <div class="d-flex align-center justify-start inline-edit-container">
                        <v-text-field
                            v-model="item.localApproxWeight"
                            type="number"
                            dense
                            outlined
                            hide-details
                            class="approx-input mr-2 rounded-lg"
                            placeholder="0.00"
                            step="0.001"
                            min="0"
                            :disabled="item.isSavingRow"
                        ></v-text-field>
                        <v-btn
                            icon
                            small
                            color="success"
                            class="elevation-1 bg-white"
                            :disabled="!isValidWeight(item.localApproxWeight) || item.isSavingRow"
                            :loading="item.isSavingRow"
                            @click="saveIndividual(item)"
                            title="Guardar peso aproximado"
                        >
                            <v-icon small>mdi-check-bold</v-icon>
                        </v-btn>
                    </div>
                </template>

                <!-- Kg Verde column -->
                <template v-slot:item.GreenWeight="{ item }">
                    <span class="font-weight-medium grey--text text--darken-3">
                        {{ item.GreenWeight !== null && item.GreenWeight !== undefined ? formatWeight(item.GreenWeight) + ' kg' : '-' }}
                    </span>
                </template>

                <!-- Cuadre Green Weight column -->
                <template v-slot:item.GreenWeightCuadre="{ item }">
                    <span class="font-weight-medium blue-grey--text text--darken-3">
                        {{ item.GreenWeightCuadre !== null && item.GreenWeightCuadre !== undefined ? formatWeight(item.GreenWeightCuadre) + ' kg' : '-' }}
                    </span>
                </template>

                <!-- Total Weight column -->
                <template v-slot:item.TotalWeight="{ item }">
                    <span class="font-weight-bold grey--text text--darken-3">
                        {{ item.TotalWeight !== null && item.TotalWeight !== undefined ? formatWeight(item.TotalWeight) + ' kg' : '-' }}
                    </span>
                </template>

                <!-- Returned Weight column -->
                <template v-slot:item.ReturnedWeight="{ item }">
                    <div class="pr-4">
                        <span v-if="item.ReturnedWeight !== null && item.ReturnedWeight !== undefined" class="font-weight-medium orange--text text--darken-4">
                            {{ formatWeight(item.ReturnedWeight) }} kg
                        </span>
                        <span v-else class="grey--text">-</span>
                    </div>
                </template>
            </v-data-table>
        </v-card>

        <!-- Prorrateo Dialog Modal -->
        <v-dialog v-model="showProrrateoModal" max-width="550" persistent rounded="lg">
            <v-card class="rounded-lg overflow-hidden">
                <v-card-title class="d-flex align-center py-4 px-6 bg-gradient-modal white--text">
                    <v-icon color="white" class="mr-2">mdi-calculator</v-icon>
                    <span class="text-h6 font-weight-bold">Prorrateo KG de Verde Proceso</span>
                </v-card-title>

                <v-card-text class="py-6 px-6">
                    <div class="mb-4 d-flex justify-space-between align-center py-3 px-4 rounded-lg bg-teal-lightest border-teal">
                        <div>
                            <div class="text-caption teal--text text--darken-3 font-weight-medium">Filas Seleccionadas</div>
                            <div class="text-h6 font-weight-bold teal--text text--darken-4">{{ selectedRows.length }} registros</div>
                        </div>
                        <div>
                            <div class="text-caption teal--text text--darken-3 font-weight-medium text-right">Total Jabas Físicas</div>
                            <div class="text-h6 font-weight-bold teal--text text--darken-4 text-right">{{ totalJabasSelected }} jabas</div>
                        </div>
                    </div>

                    <p class="text-body-2 grey--text text--darken-2 mb-6">
                        Ingrese el peso total físico real que desea prorratear. Este peso se distribuirá de forma proporcional
                        entre las filas seleccionadas.
                        <!--  según la cantidad de jabas. La última fila absorberá cualquier residuo por redondeo decimal -->
                    </p>

                    <v-text-field
                        label="Total de Kilos a Prorratear (kg)"
                        v-model="totalKilosToProrrate"
                        type="number"
                        outlined
                        dense
                        placeholder="Ej. 921.285"
                        step="0.001"
                        min="0.001"
                        class="rounded-lg font-weight-bold"
                        :disabled="isProrrating"
                        prepend-inner-icon="mdi-scale"
                    ></v-text-field>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="py-4 px-6 grey lighten-4 d-flex justify-end">
                    <v-btn
                        color="grey darken-1"
                        text
                        class="px-4 font-weight-bold"
                        @click="closeProrrateoModal"
                        :disabled="isProrrating"
                    >
                        Cancelar
                    </v-btn>
                    <v-btn
                        color="success"
                        class="px-6 font-weight-bold rounded-lg"
                        elevation="1"
                        :loading="isProrrating"
                        :disabled="!isValidProrrateWeight(totalKilosToProrrate)"
                        @click="executeProrrateo"
                    >
                        <v-icon left small>mdi-calculator-variant</v-icon>
                        Procesar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import _sTurnService from "@/services/FreshProduction/PrfTurnService.js";
import _sFrzGreenProcessHistory from "@/services/FrozenProduction/FrzGreenProcessHistory.js";
import SSelectDefinition from "@/components/Utils/SSelectDefinition";

export default {
    name: "GreenProcessManagement",
    components: {
        SSelectDefinition
    },
    data() {
        return {
            loading: false,
            activeTurnId: null,
            activeTypeCultive: null,
            activeTurnName: "Buscando turno activo...",
            items: [],
            selectedRows: [],
            showProrrateoModal: false,
            totalKilosToProrrate: "",
            isProrrating: false,
            headers: [
                { text: "Id Transferencia", value: "DlpID", width: "8%", sortable: true },
                { text: "Túnel", value: "TunnelName", width: "10%" },
                { text: "Línea", value: "LineName", width: "12%" },
                { text: "Periodo", value: "PeriodNumber", width: "8%", align: "center" },
                { text: "Jabas Verdes", value: "JabasVerdes", width: "10%", align: "right" },
                { text: "Peso Aprox Jaba (kg)", value: "ApproxWeight", width: "18%", sortable: false },
                { text: "Kg Verde", value: "GreenWeight", width: "11%", align: "right" },
                { text: "Kg Verde Cuadre", value: "GreenWeightCuadre", width: "11%", align: "right" },
                { text: "Peso Físico Total", value: "TotalWeight", width: "11%", align: "right" },
                { text: "Peso Devuelto", value: "ReturnedWeight", width: "11%", align: "right", class: "pr-6" }
            ]
        };
    },
    computed: {
        totalJabasSelected() {
            return this.selectedRows.reduce((sum, r) => sum + r.JabasVerdes, 0);
        }
    },
    created() {
        this.loadActiveTurn();
    },
    methods: {
        async loadActiveTurn() {
            this.loading = true;
            try {
                const turnResp = await _sTurnService.turnActive({ TypeArea: 4 }, this.$fun.getUserID());
                if (turnResp.status === 200 && turnResp.data.length > 0) {
                    const turnId = turnResp.data[0].TrnID;
                    const typeCultive = turnResp.data[0].TypeCultive;
                    this.activeTurnId = turnId;
                    this.activeTypeCultive = typeCultive;
                    this.activeTurnName = turnResp.data[0].TrnDescription || `Turno #${turnId}`;
                    this.search();
                } else {
                    this.activeTurnName = "No hay turno activo";
                    this.$fun.alert("No se encontró un turno activo de producción congelada.", "warning");
                }
            } catch (e) {
                console.error("Error al cargar turno activo:", e);
                this.$fun.alert("Error de conexión al cargar datos del turno.", "error");
            } finally {
                this.loading = false;
            }
        },
        async search() {
            if (!this.activeTurnId) return;
            this.loading = true;
            try {
                const filter = {
                    TrnID: this.activeTurnId,
                    TypeCultive: this.activeTypeCultive,
                    ClpID: null,
                    PchID: null
                };
                const resp = await _sFrzGreenProcessHistory.list(filter, this.$fun.getUserID());
                if (resp.status === 200) {
                    this.items = resp.data.map(item => ({
                        ...item,
                        PeriodNumber: item.PeriodNumber || item.PchPeriodo,
                        localApproxWeight: item.ApproxWeight > 0 ? item.ApproxWeight : "",
                        isSavingRow: false
                    }));
                    // Clear selected rows that might no longer be in the results
                    this.selectedRows = [];
                }
            } catch (e) {
                console.error("Error loading green process list:", e);
                this.$fun.alert("Error al obtener listado de verde proceso", "error");
            } finally {
                this.loading = false;
            }
        },
        isValidWeight(val) {
            if (val === undefined || val === null || val === "") return false;
            const parsed = parseFloat(val);
            return !isNaN(parsed) && parsed >= 0;
        },
        isValidProrrateWeight(val) {
            if (val === undefined || val === null || val === "") return false;
            const parsed = parseFloat(val);
            return !isNaN(parsed) && parsed > 0;
        },
        formatWeight(val) {
            if (val === undefined || val === null || val === "") return "-";
            return parseFloat(val).toFixed(3);
        },
        async saveIndividual(row) {
            if (!this.isValidWeight(row.localApproxWeight)) return;
            row.isSavingRow = true;
            try {
                const payload = {
                    DlpID: row.DlpID,
                    ApproxWeight: parseFloat(row.localApproxWeight)
                };
                const resp = await _sFrzGreenProcessHistory.saveIndividual(payload, this.$fun.getUserID());
                if (resp.status === 200) {
                    this.$fun.alert("Peso aproximado guardado correctamente", "success");
                    this.search();
                }
            } catch (e) {
                console.error(e);
                const errMsg = e.response?.data?.Message || e.message || "Error al guardar peso aproximado";
                this.$fun.alert(errMsg, "error");
            } finally {
                row.isSavingRow = false;
            }
        },
        closeProrrateoModal() {
            this.showProrrateoModal = false;
            this.totalKilosToProrrate = "";
        },
        async executeProrrateo() {
            if (!this.isValidProrrateWeight(this.totalKilosToProrrate)) return;
            this.isProrrating = true;
            try {
                const rowsPayload = this.selectedRows.map(r => ({
                    DlpID: r.DlpID,
                    JabasVerdes: r.JabasVerdes
                }));
                const payload = {
                    TotalKilosToProrrate: parseFloat(this.totalKilosToProrrate),
                    SelectedRowsJson: JSON.stringify(rowsPayload)
                };
                const resp = await _sFrzGreenProcessHistory.prorrateo(payload, this.$fun.getUserID());
                if (resp.status === 200) {
                    this.$fun.alert("Prorrateo masivo completado correctamente", "success");
                    this.closeProrrateoModal();
                    this.search();
                }
            } catch (e) {
                console.error(e);
                const errMsg = e.response?.data?.Message || e.message || "Error al prorratear";
                this.$fun.alert(errMsg, "error");
            } finally {
                this.isProrrating = false;
            }
        }
    }
};
</script>

<style scoped>
.bg-gradient-header {
    background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
}
.bg-gradient-modal {
    background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
}
.border-top-primary {
    border-top: 5px solid #1e3c72 !important;
}
.border-bottom {
    border-bottom: 1px solid #e0e0e0 !important;
}
.text-primary-dark {
    color: #1e3c72 !important;
}
.opacity-80 {
    opacity: 0.8;
}
.approx-input {
    max-width: 110px;
}
.inline-edit-container >>> .v-input__slot {
    min-height: 32px !important;
    padding: 0 8px !important;
}
.bg-teal-lightest {
    background-color: #e6fffa;
}
.border-teal {
    border: 1px solid #b2f5ea;
}
.green-process-table >>> th {
    background-color: #f8fafc !important;
    color: #334155 !important;
    font-weight: 700 !important;
    font-size: 0.85rem !important;
}
.green-process-table >>> tr:hover {
    background-color: #f1f5f9 !important;
}
</style>
